/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { DomController } from '../services/DomController';
import { StatusManager } from './StatusManager';

/**
 * Centralized error handling for consistent error management
 * Provides standardized error logging and user feedback
 */
export class ErrorHandler {
  /**
   * Handle an error with logging and optional UI feedback
   * @param context - Context where error occurred (e.g., "loading note")
   * @param error - Error object or message
   * @param dom - Optional DomController for UI feedback
   * @param showMessage - Whether to show error message to user (default: true)
   */
  static handle(context: string, error: Error | string, dom?: DomController, showMessage = true): void {
    const errorObj = error instanceof Error ? error : new Error(error);
    
    // Log error with context
    console.error(`Error ${context.toLowerCase()}:`, errorObj);
    
    // Show user feedback if DOM is available
    if (dom && showMessage) {
      dom.updateStatus(`Failed to ${context.toLowerCase()}.`);
    }
  }

  /**
   * Handle error with StatusManager
   * @param context - Context where error occurred
   * @param error - Error object or message
   * @param statusManager - StatusManager instance
   */
  static handleWithStatus(context: string, error: Error | string, statusManager: StatusManager): void {
    const errorObj = error instanceof Error ? error : new Error(error);
    
    // Log error with context
    console.error(`Error ${context.toLowerCase()}:`, errorObj);
    
    // Show error status
    statusManager.error(context, errorObj);
  }

  /**
   * Log informational message
   * @param context - Context for the log
   * @param message - Message to log
   */
  static log(context: string, message: string): void {
    console.log(`${context}: ${message}`);
  }

  /**
   * Log warning message
   * @param context - Context for the warning
   * @param message - Warning message
   */
  static warn(context: string, message: string): void {
    console.warn(`Warning ${context.toLowerCase()}: ${message}`);
  }

  /**
   * Handle async error in promise chain
   * @param context - Context for error handling
   * @param dom - Optional DomController
   */
  static asyncHandler(context: string, dom?: DomController) {
    return (error: Error) => {
      this.handle(context, error, dom);
    };
  }

  /**
   * Create safe wrapper for async operations
   * @param operation - Async operation to wrap
   * @param context - Context for error handling
   * @param dom - Optional DomController
   */
  static async safe<T>(
    operation: Promise<T>, 
    context: string, 
    dom?: DomController
  ): Promise<T | null> {
    try {
      return await operation;
    } catch (error) {
      this.handle(context, error as Error, dom);
      return null;
    }
  }
}
